/** Extensions the survey upload accepts: a zip archive or a single GeoTIFF. */
export const UAV_UPLOAD_EXTENSIONS: readonly string[] = ['.zip', '.tif', '.tiff', '.geotiff'];

/** Value for the file input `accept` attribute. */
export const UAV_UPLOAD_ACCEPT = '.zip,.tif,.tiff,.geotiff,application/zip,image/tiff';

/** Translation keys for upload file validation errors. */
export const UAV_UPLOAD_ERROR_ONE_FILE = 'UAV_UPLOAD_ERROR_ONE_FILE';
export const UAV_UPLOAD_ERROR_UNSUPPORTED = 'UAV_UPLOAD_ERROR_UNSUPPORTED';

/** Generic message when the server gives no usable reason. */
export const UAV_UPLOAD_FAILED = 'UAV_UPLOAD_FAILED';

/**
 * The reasons the upload endpoint returns as plain text (exact text) and the translation key that replaces each.
 * A message that is not listed here is shown as the server sent it.
 */
export const UAV_UPLOAD_SERVER_MESSAGE_KEYS: ReadonlyMap<string, string> = new Map([
  ['Unsupported file type. Upload a .zip archive or a GeoTIFF (.tif, .tiff, .geotiff).', UAV_UPLOAD_ERROR_UNSUPPORTED],
  ['File content does not match its extension.', 'UAV_UPLOAD_ERROR_CONTENT_MISMATCH'],
  ['File is empty', 'UAV_UPLOAD_ERROR_EMPTY_FILE'],
  ['The TIFF cannot be opened. Upload a valid GeoTIFF.', 'UAV_UPLOAD_ERROR_NOT_OPENABLE'],
  ['EPSG:4326 required.', 'UAV_UPLOAD_ERROR_EPSG_REQUIRED'],
  ['The GeoTIFF is too large to process.', 'UAV_UPLOAD_ERROR_TOO_LARGE'],
  ['The GeoTIFF coordinates are outside the valid longitude and latitude range.', 'UAV_UPLOAD_ERROR_INVALID_COORDINATES'],
  ['This name is already in use.', 'UAV_UPLOAD_ERROR_NAME_IN_USE'],
  ['A name is required.', 'ERROR_NAME_REQUIRED'],
]);

/** True when the file name ends with one of the supported upload extensions (case-insensitive). */
export function isSupportedUavUploadFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return UAV_UPLOAD_EXTENSIONS.some((extension) => lower.endsWith(extension));
}

export interface UavUploadPick {
  file: File | null;
  /** Translation key of the validation error, or null when the pick is valid. */
  errorKey: string | null;
}

/**
 * Picks the file to upload from a selection or a drop. Exactly one supported file is accepted;
 * several files or an unsupported extension produce an error key and no file.
 */
export function pickUavUploadFile(files: ArrayLike<File> | null | undefined): UavUploadPick {
  if (!files || files.length === 0) {
    return { file: null, errorKey: null };
  }
  if (files.length > 1) {
    return { file: null, errorKey: UAV_UPLOAD_ERROR_ONE_FILE };
  }
  const file = files[0];
  if (!isSupportedUavUploadFile(file.name)) {
    return { file: null, errorKey: UAV_UPLOAD_ERROR_UNSUPPORTED };
  }
  return { file, errorKey: null };
}

/** Upper-case extension without the dot, shown as the file format label (for example `TIF`). */
export function uavUploadFileFormat(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot + 1).toUpperCase() : '';
}

/**
 * Extracts a readable reason from a 400 response body: a plain-text body, the `detail` of a problem-details
 * body, the messages of a validation problem (`errors`), or, last, its `title`. The generic title of a
 * validation problem is only used when no specific message exists. Returns null when the body carries
 * nothing usable.
 */
export function uavUploadServerMessage(body: unknown): string | null {
  if (typeof body === 'string') {
    return body.trim() || null;
  }
  if (body && typeof body === 'object') {
    const problem = body as { detail?: unknown; title?: unknown; errors?: unknown };
    const detail = nonEmpty(problem.detail);
    if (detail) {
      return detail;
    }
    const validation = validationMessages(problem.errors);
    if (validation) {
      return validation;
    }
    return nonEmpty(problem.title);
  }
  return null;
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Joins the messages of an ASP.NET validation problem (`errors`: field name to a list of messages). */
function validationMessages(errors: unknown): string | null {
  if (!errors || typeof errors !== 'object') {
    return null;
  }
  const messages = Object.values(errors as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map(nonEmpty)
    .filter((message): message is string => message !== null);
  return messages.length ? messages.join(' ') : null;
}

/** The translation key for a known server message, or null when the message is not one of the known reasons. */
export function uavUploadServerMessageKey(message: string): string | null {
  return UAV_UPLOAD_SERVER_MESSAGE_KEYS.get(message.trim()) ?? null;
}
